import {
  createContext,
  useCallback,
  useState,
  useContext,
  FC,
  useMemo,
} from 'react';

import { ThemeProvider } from 'styled-components';

import * as themes from '~/styles/themes';

export type ThemeState = 'light' | 'dark';

interface AppThemeContext {
  currentTheme: ThemeState;
  toggleTheme(): void;
}

const AppThemeContext = createContext<AppThemeContext>({} as AppThemeContext);

const AppThemeProvider: FC = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeState>(() => {
    const storedTheme = localStorage.getItem('@Ib:theme') as ThemeState;
    return storedTheme ? JSON.parse(storedTheme) : 'light';
  });

  const toggleTheme = useCallback(() => {
    setCurrentTheme((prevTheme) => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      localStorage.setItem('@Ib:theme', JSON.stringify(newTheme));

      return newTheme;
    });
  }, []);

  const value = useMemo(
    () => ({
      currentTheme,
      toggleTheme,
    }),
    [currentTheme, toggleTheme],
  );

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={themes[currentTheme]}>{children}</ThemeProvider>
    </AppThemeContext.Provider>
  );
};

const useAppTheme = (): AppThemeContext => {
  const context = useContext(AppThemeContext);

  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }

  return context;
};

export { AppThemeProvider, useAppTheme };
